/**
 * Geocoding utilities using the French Government Address API.
 * Returns precise coordinates (private) and approximate coordinates (~1km jitter, public).
 */

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  city: string | null;
  postcode: string | null;
  label: string;
}

const API_URL = "https://api-adresse.data.gouv.fr/search/";

export async function geocodeAddress(query: string): Promise<GeocodeResult | null> {
  if (!query.trim()) return null;
  try {
    const res = await fetch(`${API_URL}?q=${encodeURIComponent(query)}&limit=1`);
    if (!res.ok) return null;
    const data = await res.json();
    const f = data?.features?.[0];
    if (!f) return null;
    const [lng, lat] = f.geometry.coordinates;
    return {
      latitude: lat,
      longitude: lng,
      city: f.properties?.city ?? null,
      postcode: f.properties?.postcode ?? null,
      label: f.properties?.label ?? query,
    };
  } catch {
    return null;
  }
}

/**
 * Add ~1km random jitter to coordinates to obfuscate the exact home location.
 * 1° latitude ≈ 111 km. 1km ≈ 0.009°.
 * We jitter randomly within a 1km radius.
 */
/**
 * Reverse geocode coordinates to get a human-readable area label
 * (neighborhood, district, locality) — without revealing exact address.
 */
export interface ReverseGeocodeResult {
  area: string | null;       // neighborhood/district (e.g. "Pont Jumeaux")
  city: string | null;
  postcode: string | null;
  context: string | null;     // e.g. "Toulouse, Haute-Garonne"
}

export async function reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResult | null> {
  try {
    const res = await fetch(
      `https://api-adresse.data.gouv.fr/reverse/?lon=${lng}&lat=${lat}&limit=1`
    );
    if (!res.ok) return null;
    const data = await res.json();
    const f = data?.features?.[0];
    if (!f) return null;
    const props = f.properties ?? {};
    // Try to extract a neighborhood-level label.
    // Strategy: use the street-level label minus the house number, fall back to city district.
    const street: string | null = props.street ?? props.name ?? null;
    return {
      area: street ?? props.district ?? null,
      city: props.city ?? null,
      postcode: props.postcode ?? null,
      context: props.context ?? null,
    };
  } catch {
    return null;
  }
}

export function approximateCoords(lat: number, lng: number): { latitude: number; longitude: number } {
  const radiusKm = 1;
  const radiusDeg = radiusKm / 111;
  // random angle and radius (uniform in disk)
  const angle = Math.random() * 2 * Math.PI;
  const r = Math.sqrt(Math.random()) * radiusDeg;
  const dLat = r * Math.cos(angle);
  const dLng = (r * Math.sin(angle)) / Math.cos((lat * Math.PI) / 180);
  return {
    latitude: +(lat + dLat).toFixed(5),
    longitude: +(lng + dLng).toFixed(5),
  };
}
